import React, { FunctionComponent } from "react";
import { useIntl } from "react-intl";

import {
  Box,
  Button,
  useColorModeValue,
} from "@chakra-ui/react";

import omit from "ramda/src/omit";

import { useToasts } from "common/toasts";
import {
  useSettingsTable,
  SettingsTable,
} from "components/settings-table";
import { IItem } from "components/settings-table/types";

import { useQuery, useMutation } from "@apollo/client";
import { SET_CHP_UNIT_SETTINGS } from "graphql/mutations";
import { GET_CHP_UNIT_SETTINGS } from "graphql/queries";
import {
  GetChpSettings,
  GetChpSettingsVariables,
} from "graphql/generated";


interface IProps {
  siteId: string;
  chpId: number;
  toggle: Function;
}


const CHPUnitSettings: FunctionComponent<IProps> = ({
  siteId,
  chpId,
  toggle,
}) => {
  const intl = useIntl();
  const { pushSuccessToast, pushErrorToast } = useToasts();

  const items: Array<IItem> = [
    {
      id: "electricEfficiency",
      type: "float",
      decimals: 0,
      unit: "%",
      name: intl.formatMessage({
        id: "chp__settings__electric_efficiency",
        defaultMessage: "Electric efficiency",
      })
    },
    {
      id: "heatEfficiency",
      type: "float",
      decimals: 0,
      unit: "%",
      name: intl.formatMessage({
        id: "chp__settings__heat_efficiency",
        defaultMessage: "Heat efficiency",
      })
    },
    {
      id: "engineHourCost",
      type: "float",
      decimals: 2,
      unit: "€",
      name: intl.formatMessage({
        id: "chp__settings__engine_hour_cost",
        defaultMessage: "Engine hour cost",
      })
    },
  ];

  const { data: controlsData, loading } = useQuery<GetChpSettings, GetChpSettingsVariables>(
    GET_CHP_UNIT_SETTINGS,
    {
      variables: { siteId, chpId },
      errorPolicy: "all",
    }
  );

  const { state, tableProps } = useSettingsTable(
    items,
    omit(["__typename"], controlsData?.getChpSettings)
  );

  const [setSettings, { loading: isSaving }] = useMutation(SET_CHP_UNIT_SETTINGS);

  return (
    <Box
      w={"full"}
      bg={useColorModeValue("gray.200", "gray.800")}
      overflow={"hidden"}
    >
      <SettingsTable {...tableProps} isDisabled={isSaving} variant="compact" />
      <Box p={1} textAlign="center">
        <Button
          size="sm"
          w={160}
          colorScheme="yellow"
          flex={1}
          isLoading={loading || isSaving}
          onClick={() => {
            setSettings({
              variables: {
                siteId,
                chpId,
                settings: state as any,
              },
            })
              .then(() => {
                toggle();
                pushSuccessToast(
                  intl.formatMessage({
                    defaultMessage: "Successfully saved",
                    id: "site__settings__save_success",
                  })
                );
              })
              .catch((reason) => {
                pushErrorToast(String(reason), null);
              });
          }}
        >
          {intl.formatMessage({
            id: "settings___apply",
            defaultMessage: "Apply",
          })}
        </Button>
      </Box>
    </Box>
  );
};

export default CHPUnitSettings;
